<ion-content style="--padding-top: 48px; --background: #f4f5f8;">
    <div class="w-full max-w-6xl mx-auto p-4">

        <!-- Loading State -->
        <div *ngIf="isLoading" class="flex flex-col items-center justify-center h-96 gap-4">
            <ion-spinner name="crescent"></ion-spinner>
            <p class="text-gray-500">{{ 'affiliate.loading' | translate }}</p>
        </div>

        <div *ngIf="!isLoading && dashboard">
            <!-- Header -->
            <div class="mb-6">
                <h1 class="text-2xl font-bold text-slate-900">🎁 {{ 'affiliate.title' | translate }}</h1>
            </div>

            <!-- Tabs -->
            <div class="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                    class="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all"
                    [class]="activeTab === 'overview' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'"
                    (click)="activeTab = 'overview'">
                    <i class="fa-solid fa-grid-2"></i>
                    {{ 'affiliate.tabOverview' | translate }}
                </button>
                <button
                    class="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all"
                    [class]="activeTab === 'referrals' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'"
                    (click)="activeTab = 'referrals'">
                    <i class="fa-solid fa-users"></i>
                    {{ 'affiliate.tabReferrals' | translate }}
                    <span *ngIf="totalInstances > 0"
                          class="ml-1 px-2 py-0.5 text-xs rounded-full"
                          [class]="activeTab === 'referrals' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-200 text-gray-600'">
                        {{ totalInstances }}
                    </span>
                </button>
                <button
                    class="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all"
                    [class]="activeTab === 'settings' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'"
                    (click)="activeTab = 'settings'">
                    <i class="fa-solid fa-bell"></i>
                    {{ 'affiliate.tabSettings' | translate }}
                </button>
            </div>

            <!-- Tab Content: Overview -->
            <div *ngIf="activeTab === 'overview'">
                <!-- How It Works -->
                <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
                    <h2 class="font-semibold text-slate-900 mb-5">{{ 'affiliate.howRewardsWork' | translate }}</h2>

                    <div class="flex flex-col md:flex-row gap-4">
                        <!-- Step 1 -->
                        <div class="flex-1 flex flex-col items-center text-center p-4 bg-gray-50 rounded-xl">
                            <div class="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-3">1</div>
                            <div class="font-semibold text-slate-900 mb-1">{{ 'affiliate.step1Title' | translate }}</div>
                            <div class="text-sm text-gray-500">{{ 'affiliate.step1Desc' | translate }}</div>
                        </div>

                        <!-- Arrow -->
                        <div class="hidden md:flex items-center text-gray-300">
                            <i class="fa-solid fa-chevron-right"></i>
                        </div>

                        <!-- Step 2 -->
                        <div class="flex-1 flex flex-col items-center text-center p-4 bg-gray-50 rounded-xl">
                            <div class="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-3">2</div>
                            <div class="font-semibold text-slate-900 mb-1">{{ 'affiliate.step2Title' | translate }}</div>
                            <div class="text-sm text-gray-500" *ngIf="rewardsInfo">
                                {{ 'affiliate.step2Desc' | translate:{credits: rewardsInfo.referredUserBonusCredits, discount: (rewardsInfo.referredUserDiscount.percentage * 100)} }}
                            </div>
                        </div>

                        <!-- Arrow -->
                        <div class="hidden md:flex items-center text-gray-300">
                            <i class="fa-solid fa-chevron-right"></i>
                        </div>

                        <!-- Step 3 -->
                        <div class="flex-1 flex flex-col items-center text-center p-4 bg-gray-50 rounded-xl">
                            <div class="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-3">3</div>
                            <div class="font-semibold text-slate-900 mb-1">{{ 'affiliate.step3Title' | translate }}</div>
                            <div class="text-sm text-gray-500" *ngIf="rewardsInfo">
                                {{ 'affiliate.step3Desc' | translate:{min: rewardsInfo.creditsPerPlan.starter, max: rewardsInfo.creditsPerPlan.profesional} }}
                            </div>
                        </div>
                    </div>

                    <!-- Rewards per Plan -->
                    <div class="mt-6 pt-5 border-t border-gray-100" *ngIf="rewardsInfo">
                        <div class="flex items-center gap-2 mb-2">
                            <i class="fa-solid fa-coins text-amber-500"></i>
                            <h3 class="font-semibold text-slate-900">{{ 'affiliate.rewardsPerPlan' | translate }}</h3>
                        </div>
                        <p class="text-sm text-gray-500 mb-4">{{ 'affiliate.rewardsPerPlanDesc' | translate }}</p>

                        <div class="bg-gray-50 rounded-lg p-4">
                            <div class="flex flex-col gap-3">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-3">
                                        <span class="w-3 h-3 rounded-full bg-blue-500"></span>
                                        <span class="text-sm text-slate-700">{{ 'affiliate.friendSubscribes' | translate }} <span class="font-semibold">Starter</span></span>
                                    </div>
                                    <span class="text-sm font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">+{{ rewardsInfo.creditsPerPlan.starter }} {{ 'affiliate.credits' | translate }}</span>
                                </div>
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-3">
                                        <span class="w-3 h-3 rounded-full bg-green-500"></span>
                                        <span class="text-sm text-slate-700">{{ 'affiliate.friendSubscribes' | translate }} <span class="font-semibold">Shared</span></span>
                                    </div>
                                    <span class="text-sm font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">+{{ rewardsInfo.creditsPerPlan.shared }} {{ 'affiliate.credits' | translate }}</span>
                                </div>
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-3">
                                        <span class="w-3 h-3 rounded-full bg-amber-500"></span>
                                        <span class="text-sm text-slate-700">{{ 'affiliate.friendSubscribes' | translate }} <span class="font-semibold">Profesional</span></span>
                                    </div>
                                    <span class="text-sm font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">+{{ rewardsInfo.creditsPerPlan.profesional }} {{ 'affiliate.credits' | translate }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Two Column Layout -->
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <!-- Left Column -->
                    <div class="lg:col-span-2 space-y-6">
                        <!-- Referral Link Card -->
                        <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                            <div class="flex items-center gap-2 mb-4">
                                <i class="fa-solid fa-link text-indigo-500"></i>
                                <h3 class="font-semibold text-slate-900">{{ 'affiliate.yourReferralLink' | translate }}</h3>
                            </div>
                            <div class="flex flex-col sm:flex-row gap-3 mb-4">
                                <input type="text"
                                       [value]="dashboard.referralLink"
                                       readonly
                                       class="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-mono" />
                                <button
                                    (click)="copyReferralLink()"
                                    class="flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors">
                                    <i [class]="copied ? 'fa-solid fa-check' : 'fa-regular fa-copy'"></i>
                                    {{ copied ? ('affiliate.copied' | translate) : ('affiliate.copy' | translate) }}
                                </button>
                            </div>
                            <div class="flex items-center gap-4 pt-4 border-t border-gray-100">
                                <span class="text-sm text-gray-500">{{ 'affiliate.shareVia' | translate }}</span>
                                <div class="flex gap-2">
                                    <button (click)="shareViaWhatsApp()"
                                            class="w-10 h-10 flex items-center justify-center rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors">
                                        <i class="fa-brands fa-whatsapp text-lg"></i>
                                    </button>
                                    <button (click)="shareViaTwitter()"
                                            class="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-500 hover:bg-gray-600 text-white transition-colors">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"/>
                                        </svg>
                                    </button>
                                    <button (click)="shareViaLinkedIn()"
                                            class="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                                        <i class="fa-brands fa-linkedin-in text-lg"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Stats -->
                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                <div class="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-3">
                                    <i class="fa-solid fa-users text-indigo-500"></i>
                                </div>
                                <div class="text-2xl font-bold text-slate-900">{{ dashboard.stats.totalReferrals }}</div>
                                <div class="text-xs text-gray-500">{{ 'affiliate.totalReferrals' | translate }}</div>
                            </div>
                            <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                <div class="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center mb-3">
                                    <i class="fa-solid fa-circle-check text-green-500"></i>
                                </div>
                                <div class="text-2xl font-bold text-slate-900">{{ dashboard.stats.convertedReferrals }}</div>
                                <div class="text-xs text-gray-500">{{ 'affiliate.convertedReferrals' | translate }}</div>
                            </div>
                            <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                <div class="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-3">
                                    <i class="fa-solid fa-clock text-amber-500"></i>
                                </div>
                                <div class="text-2xl font-bold text-slate-900">{{ dashboard.stats.pendingReferrals }}</div>
                                <div class="text-xs text-gray-500">{{ 'affiliate.pendingReferrals' | translate }}</div>
                            </div>
                            <div class="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 shadow-sm">
                                <div class="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center mb-3">
                                    <i class="fa-solid fa-bolt text-white"></i>
                                </div>
                                <div class="text-2xl font-bold text-white">{{ dashboard.stats.totalCreditsEarned }}</div>
                                <div class="text-xs text-white/80">{{ 'affiliate.totalCreditsEarned' | translate }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Tier Card -->
                    <div class="space-y-6 lg:sticky lg:top-24">
                        <!-- Tier Card -->
                        <div class="rounded-xl p-6 shadow-sm text-white"
                             [ngClass]="{
                                'tier-bronze': dashboard.tier.current === 'bronze',
                                'tier-silver': dashboard.tier.current === 'silver',
                                'tier-gold': dashboard.tier.current === 'gold',
                                'tier-diamond': dashboard.tier.current === 'diamond'
                             }">
                            <div class="flex flex-col items-center text-center mb-5">
                                <div class="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-3">
                                    <i [class]="'fa-solid text-2xl ' + getTierFaIcon(dashboard.tier.current)"></i>
                                </div>
                                <div class="text-xs uppercase tracking-wider opacity-80">{{ 'affiliate.currentTier' | translate }}</div>
                                <div class="text-2xl font-bold">{{ dashboard.tier.current | titlecase }}</div>
                            </div>

                            <div *ngIf="dashboard.tier.nextTier" class="bg-black/20 rounded-lg p-4">
                                <div class="flex justify-between text-xs mb-2">
                                    <span>{{ 'affiliate.nextMilestone' | translate }}: {{ dashboard.tier.nextTier | titlecase }}</span>
                                    <span>{{ dashboard.tier.referralsToNextTier }} {{ 'affiliate.referralsToGo' | translate }}</span>
                                </div>
                                <div class="h-2 bg-black/30 rounded-full overflow-hidden">
                                    <div class="h-full bg-white/90 rounded-full transition-all" [style.width.%]="getMilestoneProgress()"></div>
                                </div>
                            </div>

                            <div *ngIf="!dashboard.tier.nextTier" class="bg-white/20 rounded-lg p-4 flex items-center justify-center gap-2">
                                <i class="fa-solid fa-trophy"></i>
                                <span class="text-sm font-medium">{{ 'affiliate.maxTierReached' | translate }}</span>
                            </div>
                        </div>

                        <!-- Milestone Progress Card -->
                        <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-100" *ngIf="rewardsInfo">
                            <h3 class="font-semibold text-slate-900 mb-1">{{ 'affiliate.milestoneBonuses' | translate }}</h3>
                            <p class="text-xs text-gray-500 mb-5">{{ 'affiliate.milestonesDesc' | translate }}</p>

                            <!-- Progress Timeline -->
                            <div class="relative">
                                <!-- Progress Line Background -->
                                <div class="absolute top-5 left-5 right-5 h-1 bg-gray-200 rounded-full"></div>
                                <!-- Progress Line Fill -->
                                <div class="absolute top-5 left-5 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                                     [style.width.%]="getMilestoneProgressPercentage()"></div>

                                <!-- Milestones -->
                                <div class="relative flex justify-between">
                                    <!-- Start (Bronze) -->
                                    <div class="flex flex-col items-center">
                                        <div class="w-10 h-10 rounded-full flex items-center justify-center z-10 milestone-bronze">
                                            <i class="fa-solid fa-shield text-white text-sm"></i>
                                        </div>
                                        <span class="text-[10px] font-medium text-gray-500 mt-2">Start</span>
                                        <span class="text-[10px] text-gray-400">0</span>
                                    </div>

                                    <!-- Silver -->
                                    <div class="flex flex-col items-center">
                                        <div class="w-10 h-10 rounded-full flex items-center justify-center z-10"
                                             [class]="isTierAchieved('silver') ? 'milestone-silver' : 'bg-gray-200'">
                                            <i class="fa-solid fa-shield-halved text-sm"
                                               [class]="isTierAchieved('silver') ? 'text-white' : 'text-gray-400'"></i>
                                        </div>
                                        <span class="text-[10px] font-medium mt-2"
                                              [class]="isTierAchieved('silver') ? 'text-slate-700' : 'text-gray-400'">Silver</span>
                                        <span class="text-[10px] text-indigo-600 font-semibold">+{{ rewardsInfo.milestoneBonuses.silver }}</span>
                                        <span class="text-[9px] text-gray-400">{{ rewardsInfo.milestoneThresholds.silver }} conv.</span>
                                    </div>

                                    <!-- Gold -->
                                    <div class="flex flex-col items-center">
                                        <div class="w-10 h-10 rounded-full flex items-center justify-center z-10"
                                             [class]="isTierAchieved('gold') ? 'milestone-gold' : 'bg-gray-200'">
                                            <i class="fa-solid fa-medal text-sm"
                                               [class]="isTierAchieved('gold') ? 'text-white' : 'text-gray-400'"></i>
                                        </div>
                                        <span class="text-[10px] font-medium mt-2"
                                              [class]="isTierAchieved('gold') ? 'text-slate-700' : 'text-gray-400'">Gold</span>
                                        <span class="text-[10px] text-indigo-600 font-semibold">+{{ rewardsInfo.milestoneBonuses.gold }}</span>
                                        <span class="text-[9px] text-gray-400">{{ rewardsInfo.milestoneThresholds.gold }} conv.</span>
                                    </div>

                                    <!-- Diamond -->
                                    <div class="flex flex-col items-center">
                                        <div class="w-10 h-10 rounded-full flex items-center justify-center z-10"
                                             [class]="isTierAchieved('diamond') ? 'milestone-diamond' : 'bg-gray-200'">
                                            <i class="fa-solid fa-gem text-sm"
                                               [class]="isTierAchieved('diamond') ? 'text-white' : 'text-gray-400'"></i>
                                        </div>
                                        <span class="text-[10px] font-medium mt-2"
                                              [class]="isTierAchieved('diamond') ? 'text-slate-700' : 'text-gray-400'">Diamond</span>
                                        <span class="text-[10px] text-indigo-600 font-semibold">+{{ rewardsInfo.milestoneBonuses.diamond }}</span>
                                        <span class="text-[9px] text-gray-400">{{ rewardsInfo.milestoneThresholds.diamond }} conv.</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Current Progress -->
                            <div class="mt-6 p-3 bg-gray-50 rounded-lg">
                                <div class="flex items-center justify-between text-sm">
                                    <span class="text-gray-600">{{ 'affiliate.yourProgress' | translate }}</span>
                                    <span class="font-semibold text-slate-900">{{ dashboard.stats.convertedReferrals }} {{ 'affiliate.conversions' | translate }}</span>
                                </div>
                            </div>

                            <div class="flex items-center gap-2 mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                                <i class="fa-solid fa-rotate"></i>
                                {{ 'affiliate.bonusNote' | translate }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tab Content: Referrals -->
            <div *ngIf="activeTab === 'referrals'">
                <!-- Referrals Table -->
                <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" *ngIf="referredInstances.length > 0">
                    <div class="grid grid-cols-3 gap-4 px-5 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <span>{{ 'affiliate.userColumn' | translate }}</span>
                        <span>{{ 'affiliate.planColumn' | translate }}</span>
                        <span>{{ 'affiliate.dateColumn' | translate }}</span>
                    </div>
                    <div *ngFor="let item of referredInstances"
                         class="grid grid-cols-3 gap-4 px-5 py-4 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <div class="flex items-center gap-2">
                            <i class="fa-solid fa-circle-user text-gray-300 text-xl"></i>
                            <span class="text-sm text-slate-900 font-medium truncate">{{ item.user.name || item.user.email }}</span>
                        </div>
                        <div>
                            <span class="px-3 py-1 rounded-full text-xs font-medium"
                                  [ngClass]="{
                                    'bg-gray-100 text-gray-600': item.instance.plan === 'free',
                                    'bg-blue-100 text-blue-700': item.instance.plan === 'starter',
                                    'bg-green-100 text-green-700': item.instance.plan === 'shared',
                                    'bg-amber-100 text-amber-700': item.instance.plan === 'profesional',
                                    'bg-purple-100 text-purple-700': item.instance.plan === 'enterprise'
                                  }">
                                {{ item.instance.plan }}
                            </span>
                        </div>
                        <span class="text-sm text-gray-500">{{ formatDate(item.instance.createdAt) }}</span>
                    </div>

                    <!-- Pagination -->
                    <div *ngIf="totalPages > 1" class="flex items-center justify-center gap-4 py-4 border-t border-gray-100">
                        <button [disabled]="currentPage === 1"
                                (click)="loadPage(currentPage - 1)"
                                class="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <i class="fa-solid fa-chevron-left text-sm"></i>
                        </button>
                        <span class="text-sm text-gray-600">{{ currentPage }} / {{ totalPages }}</span>
                        <button [disabled]="currentPage === totalPages"
                                (click)="loadPage(currentPage + 1)"
                                class="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <i class="fa-solid fa-chevron-right text-sm"></i>
                        </button>
                    </div>
                </div>

                <!-- Empty State -->
                <div *ngIf="referredInstances.length === 0"
                     class="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
                    <i class="fa-solid fa-users text-5xl text-gray-200 mb-4"></i>
                    <h3 class="text-lg font-semibold text-gray-600 mb-1">{{ 'affiliate.noReferralsYet' | translate }}</h3>
                    <p class="text-sm text-gray-400">{{ 'affiliate.shareYourLink' | translate }}</p>
                </div>
            </div>

            <!-- Tab Content: Settings -->
            <div *ngIf="activeTab === 'settings'">
                <div class="max-w-2xl">
                    <!-- Email Notifications Card -->
                    <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <div class="flex items-center gap-3 mb-6">
                            <div class="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                                <i class="fa-solid fa-envelope text-indigo-500"></i>
                            </div>
                            <div>
                                <h3 class="font-semibold text-slate-900">{{ 'affiliate.emailNotifications' | translate }}</h3>
                                <p class="text-sm text-gray-500">{{ 'affiliate.emailNotificationsDesc' | translate }}</p>
                            </div>
                        </div>

                        <div class="space-y-4">
                            <!-- Signup Notification -->
                            <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div class="flex items-center gap-3">
                                    <i class="fa-solid fa-user-plus text-green-500"></i>
                                    <div>
                                        <div class="font-medium text-slate-900 text-sm">{{ 'affiliate.notifyOnSignup' | translate }}</div>
                                        <div class="text-xs text-gray-500">{{ 'affiliate.notifyOnSignupDesc' | translate }}</div>
                                    </div>
                                </div>
                                <label class="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox"
                                           [checked]="emailNotifications.onReferralSignup"
                                           (change)="toggleNotification('onReferralSignup')"
                                           class="sr-only peer">
                                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>

                            <!-- Conversion Notification -->
                            <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div class="flex items-center gap-3">
                                    <i class="fa-solid fa-gift text-amber-500"></i>
                                    <div>
                                        <div class="font-medium text-slate-900 text-sm">{{ 'affiliate.notifyOnConversion' | translate }}</div>
                                        <div class="text-xs text-gray-500">{{ 'affiliate.notifyOnConversionDesc' | translate }}</div>
                                    </div>
                                </div>
                                <label class="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox"
                                           [checked]="emailNotifications.onReferralConversion"
                                           (change)="toggleNotification('onReferralConversion')"
                                           class="sr-only peer">
                                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                        </div>

                        <div *ngIf="notificationsSaved" class="mt-4 p-3 bg-green-50 rounded-lg text-sm text-green-700 flex items-center gap-2">
                            <i class="fa-solid fa-check-circle"></i>
                            {{ 'affiliate.settingsSaved' | translate }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</ion-content>
